/**
 * OvertimeRequestBanner — persistent, escalating corner card that appears
 * while an employee's currently-open shift is projected to cross their
 * effective OVERTIME threshold soon. Distinct from
 * components/attendance/RequestOvertimeCard.js (always-visible static
 * policy info sitting inline on the timesheet) -- this one is fixed
 * top-right, appears on ANY page (rendered once at the DashboardLayout
 * level, not per-page), only shows while she's actually approaching, and
 * escalates to red/urgent styling within the last hour before crossing.
 *
 * Deliberately has NO "submit a request" call to action -- by the time
 * she's inside the notify window, she's normally already past
 * preapproval_notice_hours (e.g. 24h), so a compliant request literally
 * can't be submitted anymore. That CTA belongs on RequestOvertimeCard.js
 * (shown well in advance, whenever she happens to be on the timesheet),
 * not here. This is purely informational: a heads-up, nothing to act on.
 *
 * Polls GET /attendance/overtime-policy/me/ (same endpoint
 * RequestOvertimeCard reads) for its `approach` field. That field is null
 * whenever there's nothing to show -- no open shift in a notify window,
 * or she already dismissed the one that was showing (see below). Dismiss
 * (X) marks the underlying bell notification read server-side via the
 * same endpoint NotificationBell uses -- since that notification only
 * ever fires once per open shift per threshold
 * (TimeClock.overtime_notified_at/overtime_extended_notified_at never
 * reset), dismissing means it will not reappear for this same shift.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { AlertTriangle, X } from 'lucide-react';

const POLL_MS = 60_000;

export default function OvertimeRequestBanner() {
  const [approach, setApproach] = useState(null);

  const fetchStatus = useCallback(() => {
    api.get('/attendance/overtime-policy/me/')
      .then((r) => setApproach(r.data?.eligible ? r.data.approach : null))
      .catch(() => setApproach(null));
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(id);
  }, [fetchStatus]);

  if (!approach) return null;

  const { urgent, extended, threshold_hours: threshold, remaining_hours: remaining, notification_id } = approach;

  const dismiss = () => {
    if (notification_id) {
      api.post(`/notifications/${notification_id}/mark_read/`).catch(() => {});
    }
    setApproach(null);
  };

  return (
    <div
      className={`fixed top-20 right-4 z-50 w-80 rounded-lg border shadow-lg p-4 ${
        urgent ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300'
      }`}
      data-testid="overtime-approach-banner"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className={`h-4 w-4 shrink-0 mt-0.5 ${urgent ? 'text-red-600' : 'text-amber-600'}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${urgent ? 'text-red-800' : 'text-amber-800'}`}>
            {urgent ? 'Overtime threshold approaching soon' : 'Approaching your overtime threshold'}
          </p>
          <p className={`text-xs mt-1 ${urgent ? 'text-red-700' : 'text-amber-700'}`}>
            {`You're projected to reach your ${extended ? 'extended ' : ''}overtime threshold `}
            {`(${threshold}h) in about ${remaining}h`}
            {urgent ? ' — hours worked past this point will be recorded as overtime.' : '.'}
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className={`shrink-0 ${urgent ? 'text-red-500 hover:text-red-700' : 'text-amber-500 hover:text-amber-700'}`}
          aria-label="Dismiss"
          data-testid="overtime-approach-banner-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
